import Vue, {
  PropType,
  CreateElement,
  VueConstructor,
  VNode,
  VNodeChildren,
  VNodeData,
  RenderContext,
} from 'vue';
import { ScopedSlot } from 'vue/types/vnode';
import OverlayButton from '../../components/OverlayButton.ts.vue';
import InTextButton from '../../components/InTextButton.ts.vue';
import { ActionDescriptor } from '../../../controllers/VideoOverlayPresentation';

type TranslatorFunction = (path: string, ...args: any[]) => string;

const getRenderComponent = (style: ActionDescriptor['style']): VueConstructor => {
  switch (style) {
    case 'InTextButton':
      return InTextButton as unknown as VueConstructor;
    case 'OverlayButton':
    default:
      return OverlayButton as unknown as VueConstructor;
  }
};

const createData = (h: CreateElement, descriptor: ActionDescriptor, ctx: RenderContext, t: TranslatorFunction, index: number): VNodeData => {
  const renderIfExist = <T extends 'head' | 'tail'>(item: T): {} | Record<T, ScopedSlot> => {
    const value = t(`action.${index}.${item}`, { defaultValue: '' });
    if (!value) return {};
    return {
      [item]: (): VNode => h('span', value),
    };
  };

  return ({
    on: {
      click: (event: Event): void => {
        const listenerOrListeners = ctx.listeners['action-click'];
        const listeners = Array.isArray(listenerOrListeners) ? listenerOrListeners : [listenerOrListeners];
        listeners.forEach((listener) => {
          listener(descriptor, event);
        });
      },
    },
    scopedSlots: {
      ...(renderIfExist('head')),
      ...(renderIfExist('tail')),
    },
  });
};
const createChildren = (h: CreateElement, descriptor: ActionDescriptor, ctx: RenderContext, t: TranslatorFunction, index: number): VNodeChildren | null => {
  const renderTemplateIfExist = (item: 'head' | 'tail', index: number): VNode | null => {
    const value = t(`action.${index}.${item}`, { defaultValue: '' });
    if (!value) return null;
    return h('template', {
      scopedSlots: {
        [item]: (): VNode => h('span', value),
      },
    });
  };

  return [
    renderTemplateIfExist('head', index),
    t(`action.${index}.label`),
    renderTemplateIfExist('tail', index),
  ].filter(Boolean);
};

const createAction = (h: CreateElement, t: TranslatorFunction, ctx: RenderContext) =>
  (descriptor: ActionDescriptor, index: number): VNode =>
    h(
      getRenderComponent(descriptor.style),
      createData(h, descriptor, ctx, t, index),
      createChildren(h, descriptor, ctx, t, index),
    )
;

export const ActionDescriptorsRenderer = Vue.extend({
  functional: true,
  props: {
    descriptors: {
      type: Array as PropType<ActionDescriptor[]>,
      required: true,
    },
    translator: {
      type: Function as PropType<TranslatorFunction>,
      required: true,
    },
  },
  render(h, context) {
    const { descriptors, translator } = context.props;
    return descriptors.map(createAction(h, translator, context));
  },
});
